import React from 'react';
import { Modal, useResponsive } from '@sweetgreen/garnish';

import type { DeliveryPreferenceType } from '../../graphql/types';
import { OverlayToggleState, useOverlaysMachine } from '../Overlays';
import { DeliveryPreferencesForm } from './DeliveryPreferencesForm';
import { DeliveryPreferencesItem } from './DeliveryPreferencesItem';

const DELIVERY_PREFERENCES_OVERLAY_ID = 'delivery_preferences';

export const DeliveryPreferencesSelector = ({
  preferences,
  submitting,
  onSave,
}: DeliveryPreferencesSelectorProps) => {
  const { currentBreakpoint } = useResponsive();
  const { overlayId, sendOverlayEvent } = useOverlaysMachine();
  const openModal = React.useCallback(() => {
    sendOverlayEvent({
      overlayId: DELIVERY_PREFERENCES_OVERLAY_ID,
      toggleState: OverlayToggleState.OPEN,
    });
  }, [sendOverlayEvent]);
  const closeModal = React.useCallback(() => {
    sendOverlayEvent({
      overlayId: DELIVERY_PREFERENCES_OVERLAY_ID,
      toggleState: OverlayToggleState.CLOSE,
    });
  }, [sendOverlayEvent]);
  const saveForm = React.useCallback(
    (payload: DeliveryPreferences) => {
      onSave(payload);
    },
    [onSave],
  );

  React.useEffect(() => {
    if (submitting) return;
    closeModal();
  }, [submitting, closeModal]);

  return (
    <>
      <DeliveryPreferencesItem preferences={preferences} onPress={openModal} />
      <Modal
        animationType="fade"
        size={currentBreakpoint.isXS ? 'full' : 'small'}
        visible={overlayId === DELIVERY_PREFERENCES_OVERLAY_ID}
        onRequestClose={closeModal}
      >
        <DeliveryPreferencesForm
          preferences={preferences}
          submitting={submitting}
          onSave={saveForm}
          onClose={closeModal}
        />
      </Modal>
    </>
  );
};

export type DeliveryPreferences = {
  readonly type: DeliveryPreferenceType;
  readonly notes?: string;
};

type DeliveryPreferencesSelectorProps = {
  readonly preferences: DeliveryPreferences;
  readonly submitting?: boolean;
  readonly onSave: (preferences: DeliveryPreferences) => void;
};
